import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllPoets } from '@/services/poetService';
import SimplePoetSelector from '@/components/SimplePoetSelector';
import type { Database } from '@/types/supabase';
import { fetchWithAuth } from '@/lib/apiUtils';

// Define Poet type locally
type Poet = Database['public']['Tables']['poets']['Row'];

function ManageData() {
    // State for Add Poet form
    const [poetName, setPoetName] = useState('');
    const [poetryUrl, setPoetryUrl] = useState('');
    const [addPoetStatus, setAddPoetStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error', message: string }>({ type: 'idle', message: '' });

    // State for Add Poem form
    const [poemTitle, setPoemTitle] = useState('');
    const [poemPoetName, setPoemPoetName] = useState('');
    const [poemContent, setPoemContent] = useState('');
    const [poemSubject, setPoemSubject] = useState('');
    const [addPoemStatus, setAddPoemStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error', message: string }>({ type: 'idle', message: '' });

    // State for Regenerate Style Points
    const [allPoets, setAllPoets] = useState<Poet[]>([]);
    const [isLoadingPoets, setIsLoadingPoets] = useState(true);
    const [selectedPoetToRegenerate, setSelectedPoetToRegenerate] = useState<Poet | null>(null);
    const [regenerateStatus, setRegenerateStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error', message: string }>({ type: 'idle', message: '' });
    const [regenerateSearchQuery, setRegenerateSearchQuery] = useState("");

    // Fetch poets when component mounts for the regeneration selector
    useEffect(() => {
        const fetchPoets = async () => {
            setIsLoadingPoets(true);
            try {
                const poets = await getAllPoets();
                setAllPoets(poets);
            } catch (error) {
                console.error("Error fetching poets for regeneration selector:", error);
            }
            setIsLoadingPoets(false);
        };
        fetchPoets();
    }, []);

    // Filter poets for the regeneration selector based on search query
    const filteredPoetsForRegen = useMemo(() => {
        if (!regenerateSearchQuery) {
            // Show all poets initially or when search is cleared (sorted alphabetically)
            return [...allPoets].sort((a, b) => a.name.localeCompare(b.name));
        }
        const lowerCaseQuery = regenerateSearchQuery.toLowerCase();
        return allPoets.filter(poet => 
            poet.name.toLowerCase().includes(lowerCaseQuery)
        ).sort((a, b) => a.name.localeCompare(b.name)); // Keep sorted
    }, [allPoets, regenerateSearchQuery]);

    // Search handler for the regeneration selector
    const handleRegenSearch = useCallback((query: string) => {
        setRegenerateSearchQuery(query);
    }, []);

    // Handler for adding a poet
    const handleAddPoet = async (event: React.FormEvent) => {
        event.preventDefault();
        setAddPoetStatus({ type: 'loading', message: 'Adding poet... (includes style point generation)' });

        try {
            const response = await fetchWithAuth('/api/poets/admin/add-poet', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: poetName, url: poetryUrl }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            if (response.status === 200 && data.message === 'Poet already exists') {
                setAddPoetStatus({ type: 'success', message: `Poet "${data.poet?.name || poetName}" already exists (ID: ${data.poet?.id}).` });
            } else if (response.status === 201) {
                setAddPoetStatus({ type: 'success', message: `Poet "${data.poet?.name}" created successfully with style points (ID: ${data.poet?.id}).` });
                const poets = await getAllPoets();
                setAllPoets(poets);
            } else {
                setAddPoetStatus({ type: 'success', message: data.message || 'Poet processed.' });
            }

        } catch (error) {
            console.error("Error adding poet:", error);
            setAddPoetStatus({ type: 'error', message: error instanceof Error ? error.message : 'An unknown error occurred while adding poet.' });
        }
    };

    // Handler for adding a protected poem
    const handleAddPoem = async (event: React.FormEvent) => {
        event.preventDefault();
        setAddPoemStatus({ type: 'loading', message: 'Adding poem...' });

        try {
            const response = await fetchWithAuth('/api/poems/admin/submit-protected-poem', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    title: poemTitle, 
                    poetName: poemPoetName, 
                    content: poemContent, 
                    subject: poemSubject 
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }
            
            setAddPoemStatus({ type: 'success', message: `Poem "${data.title}" created successfully (ID: ${data.id}). Highlight generation triggered.` });

        } catch (error) {
            console.error("Error adding protected poem:", error);
            setAddPoemStatus({ type: 'error', message: error instanceof Error ? error.message : 'An unknown error occurred.' });
        }
    };

    // Handler for regenerating style points
    const handleRegenerateStylePoints = async () => {
        if (!selectedPoetToRegenerate) {
            setRegenerateStatus({ type: 'error', message: 'Please select a poet first.' });
            return;
        }

        setRegenerateStatus({ type: 'loading', message: `Regenerating style points for ${selectedPoetToRegenerate.name}...` });

        try {
            const poetId = selectedPoetToRegenerate.id;
            const apiUrl = `/api/poets/admin/${poetId}/regenerate-style-points`;
            console.log(`[ManageData - /admin] Calling fetchWithAuth with URL: ${apiUrl}`);

            // Define and log options before passing
            const fetchOptions = {
              method: 'PUT',
            };
            console.log("[ManageData - /admin] Options PASSED to fetchWithAuth:", fetchOptions);

            const response = await fetchWithAuth(apiUrl, fetchOptions);

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            setRegenerateStatus({ type: 'success', message: data.message || `Successfully regenerated style points for ${data.poet?.name || selectedPoetToRegenerate.name}.` });

        } catch (error) {
            console.error(`Error regenerating style points for ${selectedPoetToRegenerate.name}:`, error);
            setRegenerateStatus({ type: 'error', message: error instanceof Error ? error.message : 'An unknown error occurred during regeneration.' });
        }
    };

    return (
        <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
            <h1>Manage Poet Data</h1>

            {/* Add Poet Form */}
            <section style={{ marginBottom: '40px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
                <h2>Add New Poet</h2>
                <form onSubmit={handleAddPoet}>
                    <div style={{ marginBottom: '10px' }}>
                        <label htmlFor="poetName" style={{ display: 'block', marginBottom: '5px' }}>Poet Name:</label>
                        <input
                            type="text"
                            id="poetName"
                            value={poetName}
                            onChange={(e) => setPoetName(e.target.value)}
                            required
                            style={{ width: '300px', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="poetryUrl" style={{ display: 'block', marginBottom: '5px' }}>Poetry Foundation URL (Optional):</label>
                        <input
                            type="url"
                            id="poetryUrl"
                            value={poetryUrl}
                            onChange={(e) => setPoetryUrl(e.target.value)}
                            placeholder="https://www.poetryfoundation.org/poets/..."
                            style={{ width: '400px', padding: '8px' }}
                        />
                    </div>
                    <button type="submit" disabled={addPoetStatus.type === 'loading'} style={{ padding: '10px 15px' }}>
                        {addPoetStatus.type === 'loading' ? 'Adding...' : 'Add Poet'}
                    </button>
                </form>
                {addPoetStatus.type !== 'idle' && (
                    <p style={{ marginTop: '10px', color: addPoetStatus.type === 'error' ? 'red' : 'green' }}>
                        {addPoetStatus.message}
                    </p>
                )}
            </section>

            {/* Regenerate Style Points Form */}
            <section style={{ marginBottom: '40px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
                <h2>Regenerate Poet Style Points</h2>
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="regeneratePoet" style={{ display: 'block', marginBottom: '5px' }}>Select Poet:</label>
                    <SimplePoetSelector
                        poets={filteredPoetsForRegen}
                        selectedPoet={selectedPoetToRegenerate}
                        onSelectPoet={setSelectedPoetToRegenerate}
                        isLoading={isLoadingPoets}
                        onSearch={handleRegenSearch}
                        placeholder={isLoadingPoets ? "Loading poets..." : "Search or select a poet..."}
                        className="w-full max-w-md"
                        data-testid="regenerate-poet-selector"
                    />
                </div>
                <button 
                    onClick={handleRegenerateStylePoints} 
                    disabled={regenerateStatus.type === 'loading' || !selectedPoetToRegenerate || isLoadingPoets}
                    style={{ padding: '10px 15px' }}
                >
                    {regenerateStatus.type === 'loading' ? 'Regenerating...' : 'Regenerate Style Points'}
                </button>
                {regenerateStatus.type !== 'idle' && (
                    <p style={{ marginTop: '10px', color: regenerateStatus.type === 'error' ? 'red' : 'green' }}>
                        {regenerateStatus.message}
                    </p>
                )}
            </section>

            {/* Add Protected Poem Form */}
            <section style={{ marginBottom: '40px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
                <h2>Add Protected Poem</h2>
                <form onSubmit={handleAddPoem}>
                    <div style={{ marginBottom: '10px' }}>
                        <label htmlFor="poemTitle" style={{ display: 'block', marginBottom: '5px' }}>Poem Title:</label>
                        <input
                            type="text"
                            id="poemTitle"
                            value={poemTitle}
                            onChange={(e) => setPoemTitle(e.target.value)}
                            required
                            style={{ width: '400px', padding: '8px' }}
                        />
                    </div>
                     <div style={{ marginBottom: '10px' }}>
                        <label htmlFor="poemPoetName" style={{ display: 'block', marginBottom: '5px' }}>Poet Name:</label>
                        <input
                            type="text"
                            id="poemPoetName"
                            value={poemPoetName}
                            onChange={(e) => setPoemPoetName(e.target.value)}
                            required
                            placeholder="Must match an existing poet name exactly (case-insensitive check)"
                            style={{ width: '300px', padding: '8px' }}
                        />
                         <small style={{ display: 'block', marginTop: '3px' }}> (Poet will be created if they don't exist)</small>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                        <label htmlFor="poemSubject" style={{ display: 'block', marginBottom: '5px' }}>Subject (Optional):</label>
                        <input
                            type="text"
                            id="poemSubject"
                            value={poemSubject}
                            onChange={(e) => setPoemSubject(e.target.value)}
                            style={{ width: '300px', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="poemContent" style={{ display: 'block', marginBottom: '5px' }}>Content:</label>
                        <textarea
                            id="poemContent"
                            value={poemContent}
                            onChange={(e) => setPoemContent(e.target.value)}
                            required
                            rows={10}
                            style={{ width: '90%', maxWidth: '600px', padding: '8px', fontFamily: 'monospace' }}
                        />
                         <small style={{ display: 'block', marginTop: '3px' }}> (Use line breaks as they appear in the poem)</small>
                    </div>
                    <button type="submit" disabled={addPoemStatus.type === 'loading'} style={{ padding: '10px 15px' }}>
                        {addPoemStatus.type === 'loading' ? 'Adding...' : 'Add Protected Poem'}
                    </button>
                </form>
                 {addPoemStatus.type !== 'idle' && (
                    <p style={{ marginTop: '10px', color: addPoemStatus.type === 'error' ? 'red' : 'green' }}>
                        {addPoemStatus.message}
                    </p>
                )}
            </section>
        </div>
    );
}

export default ManageData; 